/* Shared chart furniture (ADR-0090). Tokens only (ADR-0007). A chart's own colours are set in
 * `chartTokens.ts`; this is the HTML around the SVG: the legend, and the pointer feedback that
 * every mark shares so the fifth chart behaves like the first. */

.chart {
  position: relative;
  min-width: 0;
  width: 100%;
}

.chart svg {
  display: block;
  overflow: visible;
}

/* A mark that opens a receipt says so under the pointer, and responds to it. Feedback, not decoration. */
.mark {
  cursor: pointer;
  transition: opacity var(--dur-1) var(--ease-std);
}

.mark:hover {
  opacity: 0.8;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 14px;
  margin: 0 0 6px;
  padding: 0;
  list-style: none;
  font-size: var(--t-min);
  color: var(--muted);
}

.legendItem {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.swatch {
  width: 9px;
  height: 9px;
  border-radius: 2px;
  flex: 0 0 auto;
  /* A legend swatch is the key to a colour: forced colours must not repaint it (WCAG 1.4.1). */
  forced-color-adjust: none;
}

@media (prefers-reduced-motion: reduce) {
  .mark {
    transition: none;
  }
}

/* A legend entry that is a control: the keyboard and screen-reader route to what a mark opens. */
.legendButton {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 2px 4px;
  margin: -2px -4px;
  border: 0;
  border-radius: 4px;
  background: none;
  font: inherit;
  font-size: var(--t-dense);
  color: var(--ink);
  cursor: pointer;
}

.legendButton:hover {
  background: var(--panel-2);
}

.legendCount {
  font-weight: 600;
  font-variant-numeric: lining-nums tabular-nums;
}

/* A sparkline sits where its card puts it; this is only its own box. `inline-flex` so the svg has no
   baseline gap beneath it. */
.spark {
  display: inline-flex;
  line-height: 0;
}

.spark svg {
  overflow: visible;
}

/* A series too short to draw, said in words where the line would be. Muted, annotation rank. */
.sparkNone {
  color: var(--muted);
  font-size: var(--t-min);
  line-height: 1.3;
  text-align: right;
}

/* The prior period's line: dashed, so it is told apart from this period's by pattern as well as by
   position, and a reader who cannot tell two greys apart still can (F-040). */
.priorLine {
  fill: none;
  stroke: var(--ink-2);
  stroke-width: 1.75;
  stroke-dasharray: 5 4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.priorDot {
  fill: var(--panel);
  stroke: var(--ink-2);
  stroke-width: 1.5;
}

.legendDash {
  width: 16px;
  height: 0;
  border-top: 2px dashed var(--ink-2);
  flex: 0 0 auto;
}

/* The interactive sparkline: a positioned box for the tooltip, and a ring on the focused drawing. */
.sparkLive {
  position: relative;
}

.sparkSvg {
  border-radius: 4px;
  outline: none;
}

.sparkSvg:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}
